import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate, formatDisplayTime, displayDayKey, getDisplayTimeZone, parseServerDate } from "src/utils/dateTime";
import { ArrowRight, ChevronDown, History, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Fragment, useState, type ReactNode } from "react";
import styled from "styled-components";
import { GD } from "src/GD";
import { humanize, isSecretKey } from "../providers/providersData";
import { Card, EmptyState, FilterChips, IconTile, Notice, Section, SkeletonRows } from "../providers/providersUi";
import { maskSecrets, ValueTree } from "../providers/ValueTree";

type TEvent = { id?: number; action?: string; created_at: string; user_id?: number; user_name?: string; data?: unknown };

const parsePayload = (value: unknown): unknown => {
	if (typeof value !== "string") return value ?? {};
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
};

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);

const actionTitle = (action?: string) => (action === "merchant_status_change" ? msg("statusChanged") : action ? humanize(action) : "Event");

const actionIcon = (action = "") => {
	if (/status/.test(action)) return <RefreshCw />;
	if (/(create|add|new)/.test(action)) return <Plus />;
	if (/(delete|remove)/.test(action)) return <Trash2 />;
	if (/(update|edit|change|set)/.test(action)) return <Pencil />;
	return <History />;
};

const dayLabel = (date: Date) => {
	const today = new Date();
	const start = (value: Date) => Date.parse(`${displayDayKey(value)}T00:00:00Z`);
	const diff = Math.round((start(today) - start(date)) / 86_400_000);
	if (diff === 0) return msg("templateToday_cf585410", { v0: getDisplayTimeZone() });
	if (diff === 1) return msg("templateYesterday_e27c374e", { v0: getDisplayTimeZone() });
	return formatDisplayDate(date, { locale: "en-US", format: { weekday: "short", month: "short", day: "2-digit", year: "numeric" } });
};

/** A short line for the collapsed event: the first plain values of its payload. */
const preview = (payload: unknown) => {
	if (!isRecord(payload)) return typeof payload === "string" && payload ? payload : "";
	return Object.entries(payload)
		.filter(([key, value]) => !key.startsWith("$__") && (typeof value === "string" || typeof value === "number" || typeof value === "boolean") && value !== "")
		.slice(0, 3)
		.map(([key, value]) => `${humanize(key)}: ${isSecretKey(key) ? "••••" : String(value)}`)
		.join(" · ");
};

const Feed = styled.div`
	font: 13px/1.5 "Euclid Circular B", sans-serif;
	color: #344054;
	.event + .event {
		border-top: 1px solid #eaecf0;
	}
	.event-head {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		width: 100%;
		padding: 12px 18px;
		border: 0;
		background: transparent;
		color: inherit;
		font: inherit;
		text-align: left;
	}
	button.event-head {
		cursor: pointer;
	}
	button.event-head:hover {
		background: #f9fafb;
	}
	button.event-head:focus-visible {
		outline: 2px solid #84adff;
		outline-offset: -2px;
	}
	.event-main {
		flex: 1;
		min-width: 0;
	}
	.event-title {
		display: flex;
		align-items: baseline;
		flex-wrap: wrap;
		gap: 2px 10px;
	}
	.event-title strong {
		color: #182230;
		font-weight: 600;
	}
	.event-meta {
		color: #667085;
		font-size: 12px;
	}
	.event-time {
		margin-left: auto;
		color: #98a2b3;
		font-size: 12px;
		white-space: nowrap;
	}
	.event-summary {
		margin-top: 4px;
		color: #475467;
		font-size: 12px;
		overflow-wrap: anywhere;
	}
	.transition {
		display: inline-flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 6px;
		margin-top: 6px;
	}
	.status {
		padding: 1px 8px;
		border: 1px solid #d0d5dd;
		border-radius: 6px;
		background: #f9fafb;
		font-size: 12px;
	}
	.status.next {
		border-color: #b2ddff;
		background: #eff8ff;
		color: #175cd3;
	}
	.status.next[data-status="active"] {
		border-color: #abefc6;
		background: #ecfdf3;
		color: #067647;
	}
	.status.next[data-status="rejected"],
	.status.next[data-status="blocked"],
	.status.next[data-status="deleted"] {
		border-color: #fecdca;
		background: #fef3f2;
		color: #b42318;
	}
	.reason {
		margin-top: 6px;
		padding-left: 10px;
		border-left: 2px solid #eaecf0;
		color: #475467;
		white-space: pre-wrap;
		overflow-wrap: anywhere;
	}
	.toggle {
		flex-shrink: 0;
		margin-top: 2px;
		color: #98a2b3;
		transition: transform 0.15s;
	}
	.event.open .toggle {
		transform: rotate(180deg);
	}
	.details {
		padding: 4px 18px 16px 64px;
	}
	pre {
		margin: 0;
		white-space: pre-wrap;
		overflow-wrap: anywhere;
		font-size: 12px;
	}
	@media (max-width: 600px) {
		.details {
			padding-left: 18px;
		}
	}
`;

const EventItem = ({ event }: { event: TEvent }) => {
	useLocale();
	const [open, setOpen] = useState(false);
	const payload = parsePayload(event.data);
	const date = parseServerDate(event.created_at) ?? new Date(NaN);
	const time = Number.isNaN(date.getTime()) ? String(event.created_at) : formatDisplayTime(date, { format: { hour: "2-digit", minute: "2-digit" } });
	const author = event.user_name ?? (event.user_id ? msg("templateUser_039247f9", { v0: event.user_id }) : "System");
	const record = isRecord(payload) ? payload : null;
	const statusChange = record && (record.old_status !== undefined || record.new_status !== undefined);
	const details = record ? Object.keys(record).some(key => !["old_status", "new_status", "reason"].includes(key)) : !!payload;

	let summary: ReactNode = null;
	if (statusChange && record) {
		summary = (
			<>
				<span className="transition">
					<span className="status">{record.old_status ? humanize(String(record.old_status)) : "—"}</span>
					<ArrowRight size={14} color="#98a2b3" aria-hidden="true" />
					<span className="status next" data-status={String(record.new_status ?? "")}>
						{record.new_status ? humanize(String(record.new_status)) : "—"}
					</span>
				</span>
				{record.reason ? <div className="reason">{String(record.reason)}</div> : null}
			</>
		);
	} else {
		const line = preview(payload);
		if (line) summary = <div className="event-summary">{line}</div>;
	}

	const head = (
		<>
			<IconTile small>{actionIcon(event.action)}</IconTile>
			<div className="event-main">
				<div className="event-title">
					<strong>{actionTitle(event.action)}</strong>
					<span className="event-meta">{msg("by")}{author}</span>
					<span className="event-time"><DateTimeTooltip value={event.created_at}>{time}</DateTimeTooltip></span>
				</div>
				{summary}
			</div>
			{details && <ChevronDown className="toggle" size={16} aria-hidden="true" />}
		</>
	);

	return (
		<div className={`event${open ? " open" : ""}`}>
			{details ? (
				<button type="button" className="event-head" aria-expanded={open} onClick={() => setOpen(!open)}>
					{head}
				</button>
			) : (
				<div className="event-head">{head}</div>
			)}
			{open && details && (
				<div className="details">{record ? <ValueTree value={maskSecrets(record) as Record<string, unknown>} /> : <pre>{String(payload)}</pre>}</div>
			)}
		</div>
	);
};

/** The website's event history as a feed grouped by day; status changes read as "old → new" with the reason. */
export const MerchantEvents = ({ merchantId, action }: { merchantId: number; action?: string }) => {
	useLocale();
	const [filter, setFilter] = useState("all");
	const [history] = GD.REQ_API.useRequest(undefined, {
		method: "events/list",
		data: { target_id: merchantId, target_type: "merchant", action },
	});
	const events: TEvent[] = Array.isArray(history?.data) ? [...history.data] : [];
	events.sort((a, b) => (parseServerDate(b.created_at)?.getTime() ?? 0) - (parseServerDate(a.created_at)?.getTime() ?? 0));
	const actions = [...new Set(events.map(event => event.action ?? ""))].filter(Boolean);
	const visible = events.filter(event => filter === "all" || event.action === filter);
	const days: { label: string; items: TEvent[] }[] = [];
	for (const event of visible) {
		const date = parseServerDate(event.created_at) ?? new Date(NaN);
		const label = Number.isNaN(date.getTime()) ? msg("unknownDate") : dayLabel(date);
		const day = days[days.length - 1];
		if (day && day.label === label) day.items.push(event);
		else days.push({ label, items: [event] });
	}

	return (
		<>
			{history?.error && <Notice tone="error">{history.error.message}</Notice>}
			{actions.length > 1 && (
				<div style={{ marginBottom: 12 }}>
					<FilterChips
						label={msg("eventType")}
						value={filter}
						onChange={setFilter}
						options={[{ id: "all", label: msg("all"), count: events.length }, ...actions.map(item => ({ id: item, label: actionTitle(item), count: events.filter(event => event.action === item).length }))]}
					/>
				</div>
			)}
			{!history ? (
				<Card>
					<SkeletonRows rows={3} />
				</Card>
			) : !history.error && !visible.length ? (
				<Card>
					<EmptyState icon={<History size={20} />} title={msg("noEventsYet")} text={msg("changesMadeToThisWebsiteWillAppearHere")} />
				</Card>
			) : (
				days.map(day => (
					<Fragment key={day.label}>
						<Section title={day.label} count={day.items.length} />
						<Card>
							<Feed>
								{day.items.map((event, index) => (
									<EventItem key={event.id ?? `${event.created_at}:${index}`} event={event} />
								))}
							</Feed>
						</Card>
					</Fragment>
				))
			)}
		</>
	);
};
